import React, { useCallback, useEffect, useState } from "react";
import { MdAdd } from "react-icons/md";
import Swal from "sweetalert2";
import auteurService from "../../../service/auteur.service";
import AuthorCreationModal from "./components/AuthorCreationModal";
import SearchBar from "./components/SearchBar";
import ViewToggle from "./components/ViewToggle";
import AuthorList from "./components/AuthorList";
import LoadingSpinner from "./components/LoadingSpinner";
import EmptyState from "./components/EmptyState";

const Auteurs = () => {
  const [auteurs, setAuteurs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [isGridView, setIsGridView] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [editingAuthor, setEditingAuthor] = useState(null);

  // Fetch authors
  useEffect(() => {
    const fetchAuteurs = async () => {
      setIsLoading(true);
      try {
        const data = await auteurService.getAll(100);
        setAuteurs(data);
      } catch (error) {
        console.error("Erreur lors du chargement des auteurs:", error);
        Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Impossible de charger les auteurs",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuteurs();
  }, []);

  // Reset page when itemsPerPage or searchTerm changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage, searchTerm]);

  // Handler functions
  const handleOpenModal = () => {
    setEditingAuthor(null); // Reset editing author when adding new
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingAuthor(null);
  };

  const handleItemsPerPageChange = (e) =>
    setItemsPerPage(Number(e.target.value));

  const handleSearchChange = useCallback((value) => setSearchTerm(value), []);

  const handleViewToggle = useCallback((isGrid) => setIsGridView(isGrid), []);

  // Handle author creation success
  const handleAuthorCreated = useCallback((newAuthor) => {
    setAuteurs((prev) => [...prev, newAuthor]);

    Swal.fire({
      icon: "success",
      title: "Auteur ajouté",
      text: "L'auteur a été ajouté avec succès",
      confirmButtonColor: "#57A863",
    });
  }, []);

  // Handle author update success
  const handleAuthorUpdated = useCallback((updatedAuthor) => {
    setAuteurs((prev) =>
      prev.map((author) =>
        author.id_aut === updatedAuthor.id_aut ? updatedAuthor : author
      )
    );

    Swal.fire({
      icon: "success",
      title: "Auteur modifié",
      text: "Les informations de l'auteur ont été mises à jour avec succès",
      confirmButtonColor: "#57A863",
    });
  }, []);

  // Edit author handler
  const handleEditAuthor = useCallback((author) => {
    setEditingAuthor(author);
    setModalOpen(true);
  }, []);

  // Delete author handler
  // Corrected handleDeleteAuthor function
  const handleDeleteAuthor = useCallback(
    async (author) => {
      // Show SweetAlert confirmation
      const result = await Swal.fire({
        title: "Êtes-vous sûr ?",
        html: `Voulez-vous vraiment supprimer <strong>${author.first_name} ${author.last_name}</strong> ?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Oui, supprimer",
        cancelButtonText: "Annuler",
        showClass: {
          popup: "animate__animated animate__fadeIn faster",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOut faster",
        },
      });

      // If user confirms deletion
      if (result.isConfirmed) {
        try {
          // Show loading indicator
          Swal.fire({
            // Removed 'await' here - we don't need to wait for this dialog to complete
            title: "Suppression en cours...",
            html: `
        <div class="flex flex-col items-center">
          <div class="relative w-16 h-16">
            <div class="absolute top-0 left-0 w-16 h-16 border-4 border-red-500 border-opacity-20 rounded-full"></div>
            <div class="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
          </div>
          <p class="mt-4 text-gray-700">Suppression en cours...</p>
        </div>
      `,
            showConfirmButton: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          // Delete author
          const response = await auteurService.deleteAuteurs(author.id_aut);

          // Close the loading dialog
          Swal.close();

          // Check if deletion was successful
          if (response && response.success !== false) {
            // Remove author from state
            setAuteurs((prev) =>
              prev.filter((a) => a.id_aut !== author.id_aut)
            );

            // Show success message
            await Swal.fire({
              icon: "success",
              title: "Supprimé !",
              text: `${author.first_name} ${author.last_name} a été supprimé avec succès.`,
              confirmButtonColor: "#57A863",
            });
          } else {
            // If API returned a success:false or error message
            throw new Error(response?.message || "Échec de la suppression");
          }
        } catch (error) {
          console.error("Erreur lors de la suppression de l'auteur:", error);

          // Close the loading dialog if it's still open
          Swal.close();

          // Show error message
          await Swal.fire({
            icon: "error",
            title: "Erreur",
            text:
              error.message ||
              "Une erreur s'est produite lors de la suppression de l'auteur.",
            confirmButtonColor: "#d33",
          });
        }
      }
    },
    [setAuteurs]
  ); // Added setAuteurs to dependency array

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800">
          {searchTerm ? `Recherche: ${searchTerm}` : "Tous les auteurs"}
        </h1>
        <button
          className="flex items-center justify-center rounded-md bg-[#57A863] px-6 py-2.5 text-white transition-all hover:bg-green-800 hover:shadow-lg"
          onClick={handleOpenModal}
        >
          <MdAdd className="mr-2 h-5 w-5" />
          Ajouter un auteur
        </button>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
        />

        <div className="flex items-center gap-2">
          <div className="mr-4 flex items-center">
            <label
              htmlFor="itemsPerPage"
              className="mr-2 text-sm text-gray-600"
            >
              Afficher :
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="focus:border-transparent rounded-lg border border-gray-300 px-3 py-2 text-gray-700 focus:ring-2 focus:ring-[#57A863]"
            >
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
              <option value={48}>48</option>
            </select>
          </div>

          <ViewToggle isGridView={isGridView} onToggle={handleViewToggle} />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner message="Chargement des auteurs..." />
      ) : (
        <AuthorList
          auteurs={auteurs}
          searchTerm={searchTerm}
          isGridView={isGridView}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          emptyComponent={
            <EmptyState
              searchTerm={searchTerm}
              message={
                searchTerm
                  ? "Essayez de modifier votre recherche"
                  : "Ajoutez un nouvel auteur pour commencer"
              }
            />
          }
          onPageChange={setCurrentPage}
          deleteAuthor={handleDeleteAuthor}
          editAuthor={handleEditAuthor}
        />
      )}

      {/* Creation/Update Modal */}
      <AuthorCreationModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        onCreateSuccess={handleAuthorCreated}
        onUpdateSuccess={handleAuthorUpdated}
        author={editingAuthor}
        isEditMode={!!editingAuthor}
      />
    </div>
  );
};

export default Auteurs;
